'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import type { Team, WeaponClass } from '@cs-shot/battle-sim';
import { RUSH_MID_MAPS } from '@cs-shot/battle-sim';
import { cn } from '../../../utils/cn';
import { FaChevronDown } from '../../icons';
import { ScreenContent, ScreenFixedTop } from '../../../screen';
import { FighterPortrait } from '../FighterPortrait';
import { readySkins } from '../spriteLayout';
import { bustCache } from '../render/battleAssetVersion';
import { TwoSideSelector } from './TwoSideSelector';
import { RushMidTabsBar } from './shared/RushMidTabsBar';
import type { RushMidTab } from './useRushMidController';
import { useRushMidSkins } from '../../../queries/useRushMidQueries';
import { useSkinPersist } from './lobby/useSkinPersist';
import { useWeaponSkinPersist } from './lobby/useWeaponSkinPersist';
import { WeaponSkinSection } from './skinPicker/WeaponSkinSection';
import { railFor, wornFor, type RailEntry } from './skinPicker/weaponRail';
import type { SetRushMidWeaponSkinRequestWeaponKeyEnum } from '../../../services/generated/Api';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './RushMid.translations';

const savedSkinIdx = (s: Team, serverSkin?: string): number => {
  const i = serverSkin ? readySkins(s).indexOf(serverSkin) : -1;
  return i >= 0 ? i : 0;
};

/** The «Боец» tab — the skin picker pulled out of the dead lobby: a T/CT switch on top and the
 *  big skin over a random battle-map cover, chevrons + swipe + dots. An explicit pick persists
 *  per side (the match reads the skin from the DB). */
export const FighterTab: FC<{ tab?: RushMidTab; onTab?: (tab: RushMidTab) => void }> = ({ tab, onTab }) => {
  const { t } = useLocalTranslation(translations);
  const { data: savedSkins } = useRushMidSkins();
  const persistSkin = useSkinPersist();
  const persistWeaponSkin = useWeaponSkinPersist();
  const [side, setSide] = useState<Team>('t');
  const saved: Partial<Record<Team, string>> = { t: savedSkins?.t ?? undefined, ct: savedSkins?.ct ?? undefined };
  const [idx, setIdx] = useState(() => savedSkinIdx(side, saved[side]));
  const [coverMap] = useState(() => RUSH_MID_MAPS[Math.floor(Math.random() * RUSH_MID_MAPS.length)]);

  const skins = readySkins(side);
  const skin = skins[idx] ?? skins[0];

  // the whole weapon-skin block is limited to skin-exchange accounts — the server sends the
  // flag and an empty catalog for everyone else, and the block is not rendered at all
  const weaponSkinsEnabled = savedSkins?.weaponSkinsEnabled === true;
  // the weapon rail is per SIDE (rifle is an AK on T, an M4 on CT), so switching sides shows
  // that side's keys without any extra picker
  const rail = railFor(side, savedSkins?.weaponCatalog ?? []);
  // local echo of the saved selection: the pick persists in the background, and re-reading the
  // query would blank the choice for a frame
  const [weaponPick, setWeaponPick] = useState<Record<string, string> | null>(null);
  const selection = weaponPick ?? savedSkins?.weapons ?? {};
  // портрет держит один ствол за раз, поэтому у него свой «показываемый» класс: выбор скина
  // любого класса переключает портрет на него, а надетыми остаются все три
  const [preview, setPreview] = useState<WeaponClass>('rifle');
  const worn = wornFor(rail, selection, preview);

  const pickWeaponSkin = (entry: RailEntry) => {
    setWeaponPick((prev) => {
      const next = { ...(prev ?? savedSkins?.weapons ?? {}) };
      if (entry.skin) next[entry.weaponKey] = entry.skin.name;
      else delete next[entry.weaponKey];
      return next;
    });
    setPreview(entry.weapon);
    persistWeaponSkin(entry.weaponKey as SetRushMidWeaponSkinRequestWeaponKeyEnum, entry.skin?.name ?? null);
  };

  // switching side (or the server value arriving) restores THAT side's saved skin. Primitive
  // deps — the `saved` literal is fresh each render and would reset the picker every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setIdx(savedSkinIdx(side, saved[side])); }, [side, savedSkins?.t, savedSkins?.ct]);

  const cycle = (delta: number) => {
    const next = (idx + delta + skins.length) % skins.length;
    setIdx(next);
    persistSkin(side, skins[next] ?? skins[0]);
  };

  const cover = bustCache(`/battle/cover_${coverMap}.webp`);

  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  const onSwipeStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    swipeRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const onSwipeEnd = (e: React.TouchEvent) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) cycle(dx < 0 ? 1 : -1);
  };

  return (
    <>
      <ScreenFixedTop title={t('lobbyTitle')}>
        {tab && onTab && <RushMidTabsBar activeTab={tab} onChange={onTab} />}
      </ScreenFixedTop>
      <ScreenContent hideLegalSection scrollKey="rush-mid-fighter">
        <div data-sound="ring">
        <div className="flex flex-col gap-4 px-4 pt-3">
          <TwoSideSelector side={side} onPick={setSide} />
          <div>
            <div className="text-sm font-medium text-label-secondary">{t('skinLabel')}</div>
            <div
              data-no-swipe-back
              style={{ height: 320 }}
              className="relative mt-2 flex items-center justify-center overflow-hidden rounded-2xl bg-bg-secondary"
              onTouchStart={onSwipeStart}
              onTouchEnd={onSwipeEnd}
            >
              <img src={cover} alt="" className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover blur-xl brightness-[0.7]" />
              <img src={cover} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-contain" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/55" />
              <button
                onClick={() => cycle(-1)}
                className="absolute start-2 z-10 flex size-10 items-center justify-center rounded-full bg-black/40 text-white active:bg-black/60"
                aria-label="prev"
              >
                <FaChevronDown size={18} className="rotate-90" />
              </button>
              <div className="absolute bottom-0 left-1/2 z-[1] -translate-x-1/2 translate-y-2">
                <FighterPortrait side={side} weapon={worn.weapon} skin={skin} weaponSkin={worn.skin?.name} heightPx={250} />
              </div>
              <button
                onClick={() => cycle(1)}
                className="absolute end-2 z-10 flex size-10 items-center justify-center rounded-full bg-black/40 text-white active:bg-black/60"
                aria-label="next"
              >
                <FaChevronDown size={18} className="-rotate-90" />
              </button>
              <div className="absolute bottom-3 z-[2] flex gap-1.5">
                {skins.map((s, i) => (
                  <span key={s} className={cn('h-1.5 rounded-full transition-all', i === idx ? 'w-4 bg-accent-blue' : 'w-1.5 bg-white/25')} />
                ))}
              </div>
            </div>
          </div>

          {weaponSkinsEnabled && <WeaponSkinSection rail={rail} selection={selection} preview={preview} onPick={pickWeaponSkin} />}
        </div>
        </div>
      </ScreenContent>
    </>
  );
};
